import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, API } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import Navbar from "../components/Navbar";
import Seo from "../components/Seo";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/errors";
import GoogleLoginButton from "../components/GoogleLoginButton";
import { ChevronLeft, Upload, Loader2, CheckCircle, IndianRupee } from "lucide-react";

const STEPS = ["Details", "Documents", "Payment"];
const LS_KEY = "hg_signup_draft";

function loadDraft() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function saveDraft(data) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch {}
}
function clearDraft() {
  try { localStorage.removeItem(LS_KEY); } catch {}
}

export default function Signup() {
  const [params] = useSearchParams();
  const resume = params.get("resume") === "1";
  const [role, setRole] = useState(params.get("role") === "worker" ? "worker" : "user");
  const [services, setServices] = useState([]);

  const init = () => {
    if (resume) {
      const saved = loadDraft();
      if (saved) return { ...saved, step: Math.max(saved.step || 0, 1) };
    }
    return {
      name: "", email: "", password: "", phone: "", address: "",
      service_id: "", step: 0,
      profilePic: "", aadharUrl: "", panUrl: "", paymentScreenshot: "",
    };
  };
  const [d, setD] = useState(init);
  const [uploading, setUploading] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);
  const [razorpayLoading, setRazorpayLoading] = useState(false);
  const [regFee, setRegFee] = useState(99);
  const fileRef = useRef(null);
  const { register } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/content").then(r => setRegFee(r.data.registration_fee ?? 99)).catch(() => {});
  }, []);

  // Persist to localStorage on every change
  useEffect(() => {
    if (role === "worker") saveDraft(d);
    else clearDraft();
  }, [d, role]);

  // Clear saved draft and reset on signout
  useEffect(() => {
    if (resume) {
      const saved = loadDraft();
      if (saved) setD({ ...saved });
    }
  }, [resume]);

  useEffect(() => { api.get("/services").then(r => setServices(r.data)); }, []);

  const triggerUpload = (target) => {
    setUploadTarget(target);
    fileRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    const target = uploadTarget;
    if (!file || !target) return;
    setUploading(target);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const base = API.replace(/\/api$/, "");
      const res = await fetch(`${base}/api/upload`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      if (data.ok && data.url) {
        setD(prev => ({ ...prev, [target === "profile" ? "profilePic" : target === "aadhar" ? "aadharUrl" : target === "pan" ? "panUrl" : "paymentScreenshot"]: data.url }));
        toast.success("Uploaded");
      } else {
        toast.error("Upload failed");
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(null);
      setUploadTarget(null);
      e.target.value = "";
    }
  };

  const payViaRazorpay = async () => {
    setRazorpayLoading(true);
    try {
      if (!resume) {
        const payload = {
          name: d.name, email: d.email, password: d.password,
          phone: d.phone, address: d.address, role: "worker",
          service_id: d.service_id, profile_pic: d.profilePic,
        };
        try {
          await register(payload);
        } catch (err) {
          const msg = getErrorMessage(err, "");
          if (!msg.includes("Email already registered")) throw err;
        }
      }
      // Save documents as draft before payment
      await api.post("/worker/submit-verification", {
        aadharUrl: d.aadharUrl, panUrl: d.panUrl, paymentScreenshotUrl: d.paymentScreenshot,
      }).catch(() => {});
      // Fake payment — mark registration fee as paid
      const order = { razorpay_order_id: `fake_${Date.now()}`, razorpay_payment_id: `fake_pay_${Date.now()}` };
      await api.post("/worker/pay-and-verify", order);
      setPaymentDone(true);
      setRazorpayLoading(false);
      toast.success("Payment successful!");
      clearDraft();
      nav("/worker");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to start payment"));
      setRazorpayLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (role === "user") {
      setLoading(true);
      try {
        const u = await register({ name: d.name, email: d.email, password: d.password, phone: d.phone, address: d.address, role: "user" });
        toast.success(`Welcome, ${u.name}!`);
        nav("/dashboard");
      } catch (err) {
        toast.error(getErrorMessage(err, "Signup failed"));
      } finally { setLoading(false); }
      return;
    }
    if (d.step === 0) {
      if (!d.name || !d.email || !d.password) { toast.error("Fill required fields"); return; }
      if (!d.service_id) { toast.error("Pick a service"); return; }
      setD(prev => ({ ...prev, step: 1 }));
      return;
    }
    if (d.step === 1) {
      if (!d.aadharUrl && !d.panUrl) { toast.error("Upload at least one ID document"); return; }
      setD(prev => ({ ...prev, step: 2 }));
      return;
    }
    if (d.step === 2) {
      if (!paymentDone) { toast.error("Complete payment first"); return; }
      setLoading(true);
      try {
        await api.post("/worker/submit-verification", {
          aadharUrl: d.aadharUrl, panUrl: d.panUrl, paymentScreenshotUrl: d.paymentScreenshot,
        });
        clearDraft();
        toast.success("Registration submitted! Awaiting admin verification.");
        nav("/worker");
      } catch (err) {
        toast.error(getErrorMessage(err, "Signup failed"));
      } finally { setLoading(false); }
      return;
    }
  };

  const nextLabel = () => {
    if (role === "user") return loading ? "Creating..." : "Create Account";
    if (d.step === 2) return loading ? "Submitting..." : "Submit for Review";
    return "Next";
  };

  return (
    <><Seo title="Sign Up" description="Create your Universal Tree account to book home services or join as a verified professional." noindex />
    <div className="min-h-screen flex flex-col bg-[#FAF9F6]">
      <div className="hidden md:block"><Navbar /></div>
      <div className="mobile-top-bar flex items-center gap-3 px-4 py-3 md:hidden">
        <button onClick={() => nav(-1)} className="grid h-9 w-9 shrink-0 place-items-center rounded-full hover:bg-[#F3F1ED]">
          <ChevronLeft size={22} />
        </button>
        <span className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full bg-[#050706] shadow-[0_4px_12px_rgba(28,69,50,0.18)]">
           <img src="/theuniversaltree-logo.jpeg" alt="" className="h-full w-full object-cover rounded-full" />
        </span>
        <h1 className="font-display text-base font-semibold text-[#1A1D1A]">Universal Tree</h1>
      </div>

      <input type="file" accept="image/*" ref={fileRef} onChange={handleFile} className="hidden" />

      <main className="flex-1 max-w-2xl mx-auto px-6 py-16 w-full">
        <p className="text-xs uppercase tracking-[0.25em] text-[#D9734E] font-bold">Create Account</p>
        <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tight mt-3">Join Universal Tree</h1>

        <Tabs value={role} onValueChange={(v) => { setRole(v); setD(prev => ({ ...prev, step: 0 })); }} className="mt-8">
          <TabsList className="bg-[#F3F1ED] rounded-sm h-12 p-1 grid grid-cols-2 w-full max-w-sm">
            <TabsTrigger value="user" className="rounded-sm data-[state=active]:bg-[#1C4532] data-[state=active]:text-white">Customer</TabsTrigger>
            <TabsTrigger value="worker" className="rounded-sm data-[state=active]:bg-[#1C4532] data-[state=active]:text-white">Professional</TabsTrigger>
          </TabsList>
        </Tabs>

        {role === "worker" && (
          <div className="flex items-center gap-2 mt-6 mb-2">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  i === d.step ? "bg-[#1C4532] text-white" :
                  i < d.step ? "bg-[#22C55E] text-white" : "bg-[#E2E0D9] text-[#646964]"
                }`}>
                  {i < d.step ? <CheckCircle size={16} /> : i + 1}
                </div>
                <span className={`text-xs ${i === d.step ? "text-[#1A1D1A] font-semibold" : "text-[#646964]"}`}>{s}</span>
                {i < 2 && <div className="w-8 h-px bg-[#E2E0D9]" />}
              </div>
            ))}
          </div>
        )}

        <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          {role === "user" && (
            <>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">Full name</label>
                <Input required value={d.name} onChange={e => setD(prev => ({...prev, name: e.target.value}))} className="rounded-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">Email</label>
                <Input type="email" required value={d.email} onChange={e => setD(prev => ({...prev, email: e.target.value}))} className="rounded-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">Password</label>
                <Input type="password" required minLength={6} value={d.password} onChange={e => setD(prev => ({...prev, password: e.target.value}))} className="rounded-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">Phone</label>
                <Input value={d.phone} onChange={e => setD(prev => ({...prev, phone: e.target.value}))} className="rounded-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">Address</label>
                <Input value={d.address} onChange={e => setD(prev => ({...prev, address: e.target.value}))} className="rounded-sm" />
              </div>
            </>
          )}

          {role === "worker" && d.step === 0 && (
            <>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">Full name</label>
                <Input required value={d.name} onChange={e => setD(prev => ({...prev, name: e.target.value}))} className="rounded-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">Email</label>
                <Input type="email" required value={d.email} onChange={e => setD(prev => ({...prev, email: e.target.value}))} className="rounded-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">Password</label>
                <Input type="password" required minLength={6} value={d.password} onChange={e => setD(prev => ({...prev, password: e.target.value}))} className="rounded-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">Phone</label>
                <Input value={d.phone} onChange={e => setD(prev => ({...prev, phone: e.target.value}))} className="rounded-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">Address</label>
                <Input value={d.address} onChange={e => setD(prev => ({...prev, address: e.target.value}))} className="rounded-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">Profile photo</label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden border border-[#E2E0D9] bg-[#F3F1ED] flex items-center justify-center shrink-0">
                    {d.profilePic ? (
                      <img src={d.profilePic} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Upload size={20} className="text-[#646964] opacity-40" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <button type="button" onClick={() => triggerUpload("profile")} disabled={uploading === "profile"} className="rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white px-4 h-9 text-sm font-semibold">
                      {uploading === "profile" ? "Uploading..." : "Upload photo"}
                    </button>
                    <p className="text-[11px] text-[#646964] mt-2 leading-relaxed">
                      Upload a clear face photo. Make sure your face is well-lit, centered, and unobstructed (no sunglasses, masks, or hats).
                    </p>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">Your service</label>
                <Select value={d.service_id} onValueChange={(v) => setD(prev => ({...prev, service_id: v}))}>
                  <SelectTrigger className="rounded-sm h-10"><SelectValue placeholder="Pick a service" /></SelectTrigger>
                  <SelectContent>
                    {services.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {role === "worker" && d.step === 1 && (
            <div className="md:col-span-2 space-y-6">
              <p className="text-sm text-[#646964]">Upload Aadhar card or PAN card for verification.</p>
              <DocumentUpload label="Aadhar Card" url={d.aadharUrl} uploading={uploading === "aadhar"} onUpload={() => triggerUpload("aadhar")} />
              <div className="text-center text-xs text-[#646964]">- OR -</div>
              <DocumentUpload label="PAN Card" url={d.panUrl} uploading={uploading === "pan"} onUpload={() => triggerUpload("pan")} />
            </div>
          )}

          {role === "worker" && d.step === 2 && (
            <div className="md:col-span-2 space-y-6">
              <div className="bg-[#F3F1ED] rounded-sm p-6 text-center">
                <p className="text-xs uppercase tracking-wider text-[#D9734E] font-bold mb-3">Registration Fee Payment</p>
                <p className="text-sm text-[#646964] mb-4">Pay ₹{regFee} registration fee to complete signup</p>

                <button type="button" onClick={payViaRazorpay} disabled={razorpayLoading || paymentDone}
                  className={`w-full max-w-xs mx-auto rounded-xl h-12 text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                    paymentDone
                      ? "bg-[#22C55E]/10 text-[#15803D] border border-[#22C55E] cursor-default"
                      : "bg-[#1C4532] hover:bg-[#143324] text-white shadow-sm"
                  }`}>
                  {paymentDone ? (
                    <><CheckCircle size={18} /> Payment Received</>
                  ) : razorpayLoading ? (
                    <><Loader2 size={16} className="animate-spin" /> Processing...</>
                  ) : (
                    <><IndianRupee size={16} /> Pay ₹{regFee} Registration Fee</>
                  )}
                </button>

              </div>
            </div>
          )}

          <div className="md:col-span-2 mt-2">
            {role === "worker" && d.step > 0 && (
              <Button type="button" onClick={() => setD(prev => ({ ...prev, step: prev.step - 1 }))} variant="outline" className="w-full mb-3 rounded-sm border-[#E2E0D9] text-[#646964] h-12">
                Back
              </Button>
            )}
            <Button type="submit" disabled={loading} className="w-full rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white py-6">
              {loading && <Loader2 size={16} className="animate-spin mr-2" />}
              {nextLabel()}
            </Button>

            {role === "user" && (
              <>
                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-[#E2E0D9]"/>
                  <span className="text-xs uppercase tracking-wider text-[#646964]">or</span>
                  <div className="flex-1 h-px bg-[#E2E0D9]"/>
                </div>
                <GoogleLoginButton label="Continue with Google" />
              </>
            )}

            <p className="mt-5 text-sm text-center text-[#646964]">
              Already have an account? <Link to="/login" className="text-[#1C4532] font-medium hover:underline">Log in</Link>
            </p>
          </div>
        </form>
      </main>
    </div>
    </>
  );
}

function DocumentUpload({ label, url, uploading, onUpload }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#646964] font-bold mb-2 block">{label}</label>
      <div className="relative rounded-sm overflow-hidden border border-[#E2E0D9] bg-[#F3F1ED] aspect-video flex items-center justify-center group">
        {url ? (
          <img src={url} alt={label} className="h-full w-full object-contain" onError={e => { e.target.style.display = "none"; }} />
        ) : (
          <div className="text-center text-[#646964] p-6">
            <Upload size={32} className="mx-auto mb-2 opacity-40" />
            <p className="text-xs">Tap to upload</p>
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
          <button type="button" onClick={onUpload} disabled={uploading}
            className="opacity-0 group-hover:opacity-100 transition-opacity rounded-sm bg-white/90 text-[#1C4532] hover:bg-white shadow-sm px-4 h-10 text-sm font-semibold flex items-center gap-2">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {uploading ? "Uploading..." : url ? "Change" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}
